import { useState } from 'react';
import { Avatar, Badge, ConsolePage, Icon, Panel, QR, QueryState, ListSkeleton, useToast } from '@/components';
import { useCourses, useRoster, useSetCheckin, useSetAttended } from '@/hooks/queries';
import { initialsOf } from '@/lib/format';
import type { RosterEntry } from '@/types/domain';

/**
 * Dedicated, mobile-first attendance desk for instructors/helpers: open the
 * check-in window (venue QR), project it on the big screen, and mark / de-check
 * learners by hand when a scan fails. De-check-in goes through a confirm.
 */
export function InstructorAttendance() {
  const { toast } = useToast();
  const { data: courses = [] } = useCourses();
  const course = courses[0];
  const courseId = course?.id;
  const rosterQuery = useRoster(courseId);
  const roster = rosterQuery.data ?? [];
  const setCheckin = useSetCheckin();
  const setAttended = useSetAttended();
  const [checkin, setCheckinState] = useState<{ open: boolean; token: string } | null>(null);
  const [search, setSearch] = useState('');
  const [bigScreen, setBigScreen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<RosterEntry | null>(null);

  const appUrl = import.meta.env.VITE_APP_URL ?? 'https://warmpaper.club';
  const venueUrl = checkin?.open && courseId ? `${appUrl}/?checkin=${checkin.token}&course=${courseId}` : '';
  const present = roster.filter((r) => r.attended).length;
  const q = search.trim().toLowerCase();
  const shown = roster.filter((r) => q === '' || `${r.name} ${r.th} ${r.unit}`.toLowerCase().includes(q));
  const bigQrSize = Math.min(440, (typeof window !== 'undefined' ? window.innerWidth : 480) - 96);

  const toggle = (open: boolean) => {
    if (!courseId) return;
    setCheckin.mutate(
      { courseId, open },
      {
        onSuccess: (res) => { setCheckinState(res); toast(open ? 'Check-in is open' : 'Check-in closed', { icon: open ? 'qr' : 'lock', tone: open ? 'sage' : 'clay' }); },
        onError: () => toast('Could not update check-in', { tone: 'clay', icon: 'x' }),
      },
    );
  };

  const setPresent = (r: RosterEntry, present: boolean) => {
    if (!courseId || !r.userId) return;
    setAttended.mutate(
      { courseId, userId: r.userId, present },
      {
        onSuccess: () => toast(present ? `${r.name} — present` : `${r.name} — removed`, { tone: present ? 'sage' : 'clay', icon: present ? 'check' : 'x' }),
        onError: (err) => {
          const msg = (err as { message?: string } | null)?.message ?? String(err);
          toast(msg.includes('lessons_incomplete') ? `${r.name} hasn’t finished the lessons` : 'Could not update attendance', { tone: 'clay', icon: 'x' });
        },
      },
    );
    setConfirmRemove(null);
  };

  return (
    <ConsolePage title="Attendance" th="เช็คอินหน้างาน" crumbs="SESSIONS" back="/teach" maxWidth={620}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Panel title="Check-in window · หน้าต่างเช็คอิน">
          {checkin?.open && venueUrl ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
              <Badge kind="sage" dot>Open — learners can scan</Badge>
              <div style={{ padding: 12, background: '#fff', borderRadius: 14, border: '1px solid var(--line)' }}>
                <QR value={venueUrl} size={196} />
              </div>
              <button className="btn btn-primary btn-block" onClick={() => setBigScreen(true)}>
                <Icon name="scan" size={16} /> Show on big screen · แสดงบนจอใหญ่
              </button>
              <p style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', margin: 0, lineHeight: 1.5, maxWidth: 320 }}>
                Project this at the venue. Learners scan to check themselves in — or tap anyone below whose scan fails.
              </p>
              <button className="btn btn-ghost btn-block" style={{ color: 'var(--danger)' }} disabled={setCheckin.isPending} onClick={() => toggle(false)}>
                <Icon name="lock" size={15} /> Close check-in
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: 13.5, color: 'var(--ink-3)', margin: 0, lineHeight: 1.5 }}>
                Open check-in to show the venue QR. While it’s open, learners scan to mark themselves present, and you can tap anyone below.
              </p>
              <button className="btn btn-primary btn-lg btn-block" disabled={setCheckin.isPending || !courseId} onClick={() => toggle(true)}>
                <Icon name="qr" size={18} /> Open check-in
              </button>
            </div>
          )}
        </Panel>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Mark present · เช็คชื่อ</span>
          <Badge kind="sage" dot>{present} / {roster.length} present</Badge>
        </div>
        <input className="input" placeholder="Search a learner…" value={search} onChange={(e) => setSearch(e.target.value)} />

        <QueryState query={rosterQuery} skeleton={<ListSkeleton rows={6} />} isEmpty={() => roster.length === 0} emptyIcon="users" emptyTitle="No learners enrolled yet">
          {() => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {shown.map((r, i) => (
                <div key={r.userId ?? i} className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 14px', borderColor: r.attended ? 'var(--sage)' : 'var(--line)', background: r.attended ? 'var(--sage-tint)' : 'var(--card)' }}>
                  <Avatar initials={initialsOf(r.name.split(' ')[0], r.name.split(' ')[1] ?? '')} size={42} tone={r.tone ?? 'slate'} photoUrl={r.photoUrl} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name || r.th}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{r.unit || '—'}</div>
                  </div>
                  {r.attended ? (
                    // Tap "Present" to de-check-in — goes through a confirm.
                    <button className="btn btn-ghost btn-sm" style={{ flex: 'none', color: 'var(--sage-deep)', fontWeight: 600 }} disabled={!r.userId || setAttended.isPending} onClick={() => setConfirmRemove(r)}>
                      <Icon name="check" size={16} /> Present
                    </button>
                  ) : r.lessons < 100 ? (
                    <span style={{ flex: 'none', fontSize: 12, color: 'var(--ink-4)', display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="lock" size={13} /> Lessons {r.lessons}%</span>
                  ) : (
                    <button className="btn btn-primary btn-sm" style={{ flex: 'none' }} disabled={!r.userId || setAttended.isPending} onClick={() => setPresent(r, true)}>Mark</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </QueryState>
      </div>

      {/* Big-screen QR — project at the venue. Tap anywhere to exit. */}
      {bigScreen && venueUrl && (
        <div role="button" tabIndex={0} onClick={() => setBigScreen(false)} onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter') setBigScreen(false); }} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'var(--paper)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 26, padding: 32, cursor: 'pointer' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="th" style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-.01em' }}>{course?.title.th}</div>
            <div style={{ fontSize: 17, color: 'var(--ink-3)', marginTop: 6 }}>สแกนเพื่อเช็คชื่อ · Scan to check in</div>
          </div>
          <div style={{ padding: 24, background: '#fff', borderRadius: 24, border: '2px solid var(--sage)', boxShadow: 'var(--sh-lg)' }}>
            <QR value={venueUrl} size={bigQrSize} />
          </div>
          <Badge kind="sage" dot>{present} / {roster.length} checked in</Badge>
          <div style={{ position: 'absolute', top: 20, right: 22, fontSize: 13, color: 'var(--ink-3)', display: 'flex', gap: 6, alignItems: 'center' }}><Icon name="x" size={16} /> Tap anywhere to exit</div>
        </div>
      )}

      {/* De-check-in confirm */}
      {confirmRemove && (
        <div role="button" tabIndex={0} onClick={(e) => { if (e.target === e.currentTarget) setConfirmRemove(null); }} onKeyDown={(e) => { if (e.key === 'Escape') setConfirmRemove(null); }} style={{ position: 'fixed', inset: 0, zIndex: 110, background: 'rgba(20,18,16,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div className="card" style={{ maxWidth: 360, width: '100%', padding: 22, display: 'flex', flexDirection: 'column', gap: 16, background: 'var(--card)' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--clay-soft, var(--paper-2))', color: 'var(--danger)', display: 'grid', placeItems: 'center', flex: 'none' }}><Icon name="x" size={20} /></div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>Remove attendance?</div>
                <div style={{ fontSize: 13.5, color: 'var(--ink-2)', marginTop: 4, lineHeight: 1.5 }}>This marks <strong>{confirmRemove.name}</strong> as not checked in. You can mark them present again anytime.</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmRemove(null)}>Cancel</button>
              <button className="btn btn-sm" style={{ background: 'var(--danger)', color: '#fff' }} disabled={setAttended.isPending} onClick={() => setPresent(confirmRemove, false)}>Remove attendance</button>
            </div>
          </div>
        </div>
      )}
    </ConsolePage>
  );
}
